import type { Campo } from '../motor/esquema'

/**
 * Lo que el constructor de formularios necesita saber de los tipos de campo, **sin llevar
 * su copia del catálogo**: los tipos, sus etiquetas y sus propiedades salen del esquema
 * que sirve el núcleo (los bloques del campo `campos`). Aquí solo hay cómo se ordenan en
 * la paleta y cuánto ocupa cada uno en una fila.
 *
 * Funciones puras, con su prueba (`catalogo.test.ts`).
 */
export type Bloque = NonNullable<Campo['bloques']>[number]

type Fila = Record<string, unknown>

/** Los grupos de la paleta, en este orden; uno que llegue del núcleo con otro nombre va detrás. */
export const GRUPOS = ['Generales', 'Avanzados', 'Estructura', 'Pago'] as const

/** El grupo lo dice el núcleo (`bloque.grupo`); un tipo que no lo traiga va a «Avanzados». */
export const grupoDe = (bloque: Bloque): string =>
  bloque.grupo || 'Avanzados'

const normal = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

/**
 * La paleta: los tipos que el núcleo **ofrece** a quien edita, por grupos y en el orden del
 * esquema dentro de cada uno, filtrados por lo que se busca («correo», «fecha»…) sin mirar
 * tildes ni mayúsculas.
 */
export const gruposDeLaPaleta = (
  bloques: Bloque[],
  busqueda = '',
): Array<{ grupo: string; bloques: Bloque[] }> => {
  const buscado = normal(busqueda)
  const ofrecidos = bloques.filter(
    (b) =>
      !b.sinAnadir &&
      (!buscado || normal(`${b.etiqueta} ${b.slug} ${b.descripcion ?? ''}`).includes(buscado)),
  )
  const grupos = new Map<string, Bloque[]>()
  for (const b of ofrecidos) grupos.set(grupoDe(b), [...(grupos.get(grupoDe(b)) ?? []), b])
  const orden = (g: string) => {
    const i = (GRUPOS as readonly string[]).indexOf(g)
    return i === -1 ? GRUPOS.length : i
  }
  return [...grupos.entries()]
    .sort(([a], [b]) => orden(a) - orden(b) || a.localeCompare(b, 'es'))
    .map(([grupo, lista]) => ({ grupo, bloques: lista }))
}

// --- El ancho de cada campo ------------------------------------------------------------

/**
 * Cuántas de las seis columnas ocupa cada ancho (el contrato con el núcleo: `ancho` en
 * cada campo). Sin ancho, o con uno que no se conoce, la fila entera.
 */
export const COLUMNAS_POR_ANCHO: Record<string, number> = {
  completo: 6,
  mitad: 3,
  tercio: 2,
  'dos-tercios': 4,
}

/** Lo que va siempre a lo ancho, diga lo que diga su `ancho`: un salto de paso parte el formulario, y un campo oculto no se ve. */
const SIEMPRE_ENTEROS = new Set(['paso', 'oculto'])

export const columnasDe = (fila: Fila): number => {
  if (SIEMPRE_ENTEROS.has(String(fila.blockType))) return 6
  return COLUMNAS_POR_ANCHO[String(fila.ancho ?? '')] ?? 6
}

/**
 * Los campos agrupados en filas como quedarán en la web: los que van seguidos y caben en
 * seis columnas comparten fila; el que no cabe empieza otra. Devuelve los índices de cada
 * fila, en orden.
 */
export const filasPorAncho = (campos: Fila[]): number[][] => {
  const filas: number[][] = []
  let ocupadas = 6
  campos.forEach((campo, i) => {
    const columnas = columnasDe(campo)
    if (ocupadas + columnas > 6 || columnas === 6) {
      filas.push([i])
      ocupadas = columnas
    } else {
      filas[filas.length - 1]!.push(i)
      ocupadas += columnas
    }
  })
  return filas
}

/** La clase de la rejilla de seis columnas para cada ancho; en un contenedor estrecho (un móvil), todo entero. */
export const CLASE_DE_COLUMNAS: Record<number, string> = {
  6: 'col-span-6',
  4: 'col-span-6 @xl:col-span-4',
  3: 'col-span-6 @xl:col-span-3',
  2: 'col-span-6 @xl:col-span-2',
}
